import boxOfferPlanIcon from "@images/illustrations/boxOfferPlanIcon.svg"
import { PencilEditIcon } from "@images/svgIcons/pencilEditIcon"
import { WarningIcon } from "@images/svgIcons/warningIcon"
import { LeadType } from "@modules/boxLead/entities/boxLeadTypes"
import { useBoxLeadDelete } from "@modules/boxLead/hooks/useBoxLeadActions"
import { useBoxLead } from "@modules/boxLead/hooks/useBoxLeadDetails"
import { useBoxLeadSidebar } from "@modules/boxLead/hooks/useBoxLeadSidebar"
import { displayRole } from "@modules/boxStaff/entities/boxStaffUtils"
import { word } from "@modules/core/utils/i18n"
import { UserCell } from "@modules/members/components/userCell"
import {
  MemberSendOfferModal,
  MemberSendOfferModalRef,
} from "@modules/members/sendOfferToMember/components/memberSendOfferModal"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { useToast } from "@modules/ui/components/huToast"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { formatDateWithHyphen } from "@modules/utils/dateUtils"
import { formatPhoneNumber } from "@modules/utils/numberUtils"
import dayjs from "dayjs"
import { CountryCode } from "libphonenumber-js"
import { CircleFadingPlus, Hourglass, Mail, Phone, Trash2 } from "lucide-react"
import { useRef } from "react"
import { useTheme } from "styled-components"

type BoxLeadOverviewProps = {
  leadId: string
  className?: string
} & React.ComponentProps<"div">

export function BoxLeadOverview({ leadId, className, ...props }: BoxLeadOverviewProps) {
  const defaultClassName = "flex flex-column w-full gap-5"

  const theme = useTheme()
  const toast = useToast()
  const confirmPopup = useConfirmPopup()

  const { hideSidebar } = useSidebar()
  const { data: leadDetails } = useBoxLead(leadId)
  const { deleteBoxLead, isPending: isPendingDelete } = useBoxLeadDelete()
  const { navigateToBoxLeadForm } = useBoxLeadSidebar()

  const memberSendOfferModalRef = useRef<MemberSendOfferModalRef>(null)
  const bodyDeletePopup = (
    <div className="flex flex-column align-items-center align-content-center justify-content-center gap-3 ">
      <div className="flex flex-column gap-1 ">
        <HUText fontStyle="TL" color={theme.mainWhite} className="flex justify-content-center text-center ">
          {word("box.lead.overview.modal.delete.header")}
        </HUText>
        <HUText fontStyle="BS" color={theme.neutralColor700} className="flex justify-content-center text-center">
          {word("box.lead.overview.modal.delete.warning")}
        </HUText>
      </div>
      <HUText fontStyle="BM" color={theme.mainWhite} className="flex justify-content-center text-center">
        {word("box.lead.overview.modal.delete.content")}
      </HUText>
    </div>
  )

  const handleSendOfferModal = () => {
    memberSendOfferModalRef.current?.open()
  }

  const onRemove = () => {
    confirmPopup.show({
      width: "418px",
      title: "",
      message: bodyDeletePopup,
      accept: async () => {
        await deleteBoxLead(leadId)
          .then(() => {
            toast.show({
              severity: "success",
              detail: word("box.lead.form.toast.delete.success"),
            })
            hideSidebar(false)
          })
          .catch(() => {
            toast.show({
              severity: "error",
              detail: word("box.lead.form.toast.delete.error"),
            })
          })
      },
      footerProps: {
        align: true,
        cancelText: word("global.cancel"),
        confirmText: word("global.delete"),
        confirmClassName: "flex-1",
        cancelClassName: "flex-1",
        footerClassName: "flex flex-row justify-content-around w-full",
      },
    })
  }

  const onEdit = () => {
    navigateToBoxLeadForm(leadDetails)
  }

  return (
    <div className={className ?? defaultClassName} {...props}>
      {/* HEADER */}
      <div className="grid">
        <div className="col-3">
          <div className="flex flex-column gap-1">
            <HUText type="label" fontStyle="LS" color={theme.neutralColor600}>
              {word("box.lead.overview.content.firstContact.label").toUpperCase()}
            </HUText>
            <HUText type="label" fontStyle="TS" color={theme.mainWhite}>
              {formatDateWithHyphen(dayjs(leadDetails.firstContact).format("L"))}
            </HUText>
          </div>
        </div>
        <div className="col-4">
          <div className="flex flex-row gap-1 align-items-center">
            <Hourglass size={24} color={leadDetails.getColorFirstContact(theme)} strokeWidth={1.5} />
            <HUText fontStyle="BM" color={theme.mainWhite}>
              {word("box.lead.overview.content.firstContact.hourglassText", {
                count: dayjs().diff(dayjs(leadDetails.firstContact), "day"),
              })}
            </HUText>
          </div>
        </div>
        <div className="col-5">
          <div className="flex flex-column gap-1">
            <HUText type="label" fontStyle="LS" color={theme.neutralColor600}>
              {word("box.lead.overview.content.madeBy.label").toUpperCase()}
            </HUText>
            {leadDetails.madeByStaff ? (
              <UserCell
                member={{
                  avatar: leadDetails.madeByStaff.user.avatar,
                  firstname: leadDetails.madeByStaff.user.firstname,
                  lastname: leadDetails.madeByStaff.user.lastname,
                }}
                subtitle={displayRole(leadDetails.madeByStaff.staffMemberRole)}
                subtitleColor={theme.neutralColor500}
              />
            ) : (
              <div className="flex flex-row gap-2 w-full align-items-center justify-content-center">
                <CircleFadingPlus size={24} color={theme.mainWhite} className="w-2" />
                <div className="flex flex-column gap-1 justify-content-center w-10">
                  <HUText fontStyle="LL" style={{ whiteSpace: "break-spaces", overflowWrap: "break-word" }}>
                    {leadDetails.leadType === LeadType.MANUAL_ADDITION
                      ? leadDetails.getWordingType()
                      : word("box.lead.overview.content.madeBy.automaticAddition")}
                  </HUText>
                  <HUText
                    fontStyle="LS"
                    color={theme.neutralColor500}
                    style={{ whiteSpace: "break-spaces", overflowWrap: "break-word" }}
                  >
                    {leadDetails.leadType === LeadType.MANUAL_ADDITION
                      ? word("box.lead.overview.content.madeBy.manualAddition")
                      : leadDetails.getWordingType()}
                  </HUText>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CONTACT */}
      <div className="flex flex-column gap-2">
        <HUText fontStyle="TM" color={theme.mainWhite}>
          {word("global.form.label.contact")}
        </HUText>
        <div className="grid ">
          <div className="col-7 flex flex-row gap-2 align-items-center">
            <Mail strokeWidth={1.5} color={theme.neutralColor600} size={22} />
            <HUText
              fontStyle="TM"
              color={theme.mainWhite}
              className="w-full overflow-hidden"
              style={{ whiteSpace: "break-spaces", overflowWrap: "break-word" }}
            >
              {leadDetails.email ?? word("box.lead.overview.content.contact.noEmail")}
            </HUText>
          </div>
          <div className="col-5 flex flex-row gap-2 align-items-center">
            <Phone strokeWidth={1.5} color={theme.neutralColor600} size={22} />
            <HUText fontStyle="TM" color={theme.mainWhite}>
              {leadDetails.phoneNumber?.number
                ? formatPhoneNumber(leadDetails.phoneNumber.number, leadDetails.phoneNumber.countryCode as CountryCode)
                : word("box.lead.overview.content.contact.noPhoneNumber")}
            </HUText>
          </div>
        </div>
      </div>

      {/* GOALS AND EXPERIENCE LEVEL */}
      <div className="flex flex-row justify-content-between">
        <div className="flex flex-column gap-2 max-w-24rem">
          <HUText type="label" fontStyle="LS" color={theme.neutralColor600}>
            {word("box.lead.overview.content.goals.label", { count: leadDetails.goals?.length ?? 1 }).toUpperCase()}
          </HUText>
          <HUText
            fontStyle="BM"
            style={{ whiteSpace: "break-spaces", overflowWrap: "break-word" }}
            color={(leadDetails.goals?.length ?? 0) > 0 ? theme.mainWhite : theme.neutralColor400}
          >
            {leadDetails.getWordingGoals()}
          </HUText>
        </div>
        <div className="flex flex-column gap-2">
          <HUText type="label" fontStyle="LS" color={theme.neutralColor600}>
            {word("box.lead.overview.content.experienceLevel.label").toUpperCase()}
          </HUText>
          <HUText fontStyle="BM" color={leadDetails.experienceLevel ? theme.mainWhite : theme.neutralColor400}>
            {leadDetails.getWordingExperienceLevel()}
          </HUText>
        </div>
      </div>

      {/* HEARD ABOUT US */}
      <div className="flex flex-column gap-2">
        <HUText type="label" fontStyle="LS" color={theme.neutralColor600}>
          {word("box.lead.overview.content.hearAboutUs.label").toUpperCase()}
        </HUText>
        <HUText fontStyle="BM" color={leadDetails.hearAboutUs ? theme.mainWhite : theme.neutralColor400}>
          {leadDetails.getWordingHeardAboutUs()}
        </HUText>
      </div>

      {/* BEST TIME TO CALL */}
      <div className="flex flex-column gap-2">
        <HUText type="label" fontStyle="LS" color={theme.neutralColor600}>
          {word("box.lead.overview.content.bestTimeToContact.label").toUpperCase()}
        </HUText>
        <HUText
          fontStyle="BM"
          color={leadDetails.bestTimeToContact ? theme.mainWhite : theme.neutralColor400}
          style={{ whiteSpace: "break-spaces", overflowWrap: "break-word" }}
        >
          {leadDetails.getWordingBestTimeToContact()}
        </HUText>
      </div>

      {/* INTERNAL NOTE*/}
      <div className="flex flex-column gap-2">
        <HUText fontStyle="TM" color={theme.mainWhite}>
          {word("global.form.label.internalNote")}
        </HUText>
        <div className="flex flex-row gap-2 mt-2 mb-2">
          <WarningIcon color={theme.neutralColor900} width={24} height={24} className="w-1" />
          <HUText fontStyle="BS" color={theme.neutralColor400}>
            {word("box.lead.form.input.internalNote.warning")}
          </HUText>
        </div>
        <HUText fontStyle="BM" color={theme.mainWhite}>
          {leadDetails.note ?? word("box.lead.overview.content.internalNote.emptyNote")}
        </HUText>
      </div>

      {/* ADD SEND OFFER */}
      {leadDetails.user?.id ? (
        <div
          onClick={handleSendOfferModal}
          className="
          shiny-hover flex mx-1 cursor-pointer
          rounded-card border-2 border-electricPurple-400
          p-2 gap-2 justify-content-center align-items-center bg-neutralColor-50
          "
        >
          <img src={boxOfferPlanIcon} />
          <HUText fontStyle="TS" color={theme.mainWhite}>
            {word("box.member.table.offer.menu.sendOffer")}
          </HUText>
        </div>
      ) : null}

      {/* BUTTONS */}
      <div className=" mb-4 flex flex-row gap-2 align-items-center justify-content-center">
        <HUButton
          type="Left_Icon"
          size="M"
          colorType="Quaternary"
          text={word("box.lead.overview.content.button.deleteLead")}
          onClick={onRemove}
          className="w-full"
          disabled={isPendingDelete}
          icon={{
            iconView: <Trash2 color={theme.mainWhite} size={15} />,
          }}
        />
        <HUButton
          type="Left_Icon"
          size="M"
          colorType="Primary"
          text={word("global.edit")}
          onClick={onEdit}
          submit
          className="w-full"
          icon={{
            iconView: <PencilEditIcon color={theme.mainWhite} size={15} />,
          }}
        />
      </div>

      {leadDetails.user?.id ? (
        <MemberSendOfferModal userId={leadDetails.user.id} ref={memberSendOfferModalRef} />
      ) : null}
    </div>
  )
}
